import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Check, CreditCard, Sparkles, Info, Zap } from 'lucide-react';
import api, { getErrorMessage } from '../lib/api';
import { cn } from '../lib/utils';
import { TIERS } from '../lib/constants';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/ui/PageHeader';
import GlassCard from '../components/ui/GlassCard';
import Button from '../components/ui/Button';
import ProgressBar from '../components/ui/ProgressBar';
import { StaggerGroup, staggerItem } from '../components/ui/Reveal';
import Tilt from '../components/fx/Tilt';
import CountUp from '../components/fx/CountUp';
import Magnetic from '../components/fx/Magnetic';
import DemoCheckout from '../components/billing/DemoCheckout';

function TierCard({ tier, isCurrent, demo, pending, onUpgrade, onCancel }) {
  const isPaid = tier.id !== 'FREE';

  let action;
  if (isCurrent) {
    action = isPaid ? (
      <Button variant="secondary" className="w-full" loading={pending === 'cancel'} onClick={onCancel}>
        {demo ? 'Cancel (demo)' : 'Manage billing'}
      </Button>
    ) : (
      <Button variant="secondary" className="w-full" disabled>
        Current plan
      </Button>
    );
  } else if (!isPaid) {
    action = (
      <Button variant="ghost" className="w-full" loading={pending === 'cancel'} onClick={onCancel}>
        Switch to Free
      </Button>
    );
  } else {
    const btn = (
      <Button
        variant={tier.highlighted ? 'primary' : 'secondary'}
        className="w-full"
        loading={pending === tier.id}
        onClick={() => onUpgrade(tier.id)}
      >
        {demo ? `Upgrade to ${tier.name}` : tier.cta}
      </Button>
    );
    action = <Magnetic strength={0.35} className="block w-full">{btn}</Magnetic>;
  }

  return (
    <motion.div variants={staggerItem}>
      <Tilt max={7} className="h-full">
        <GlassCard
          hoverable
          beam={tier.highlighted}
          className={cn('relative flex h-full flex-col p-7', tier.highlighted && 'shadow-glow')}
        >
          {tier.highlighted && (
            <span className="absolute -top-3 left-7 rounded-full bg-brand-500 px-3 py-1 text-xs font-semibold text-white">
              Most popular
            </span>
          )}
          {isCurrent && (
            <span className="absolute -top-3 right-7 rounded-full bg-success/90 px-3 py-1 text-xs font-semibold text-white">
              Current
            </span>
          )}
          <h3 className="text-lg font-semibold">{tier.name}</h3>
          <p className="text-sm text-content-subtle">{tier.tagline}</p>
          <div className="mt-4 flex items-end gap-1">
            <span className="font-display text-4xl font-semibold">
              $<CountUp value={tier.price} />
            </span>
            <span className="mb-1 text-sm text-content-subtle">/mo</span>
          </div>
          <ul className="mt-6 flex-1 space-y-3">
            {tier.features.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-content-muted">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                {f}
              </li>
            ))}
          </ul>
          <div className="mt-7">{action}</div>
        </GlassCard>
      </Tilt>
    </motion.div>
  );
}

export default function Pricing() {
  const { toast } = useToast();
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [pending, setPending] = useState(null);
  const [checkoutPlan, setCheckoutPlan] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: async () => (await api.get('/billing/status')).data,
  });
  const currentPlan = data?.plan || user?.plan || 'FREE';
  const demo = !data?.configured; // unconfigured Stripe => demo billing

  useEffect(() => {
    const status = params.get('status');
    if (status === 'success') toast.success('Subscription updated — welcome aboard!');
    if (status === 'cancelled') toast.info('Checkout cancelled.');
    if (status) {
      params.delete('status');
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncAfterChange = async () => {
    await refresh(); // updates user.plan in the sidebar
    qc.invalidateQueries({ queryKey: ['billing', 'status'] });
    qc.invalidateQueries({ queryKey: ['stats'] });
    setPending(null);
  };

  const upgrade = async (planId) => {
    // Demo billing → open the realistic (fake) checkout modal.
    if (demo) {
      setCheckoutPlan(planId);
      return;
    }
    // Real Stripe → redirect to the hosted checkout page.
    setPending(planId);
    try {
      const { data: res } = await api.post('/billing/checkout', { plan: planId });
      window.location.href = res.url;
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not start upgrade.'));
      setPending(null);
    }
  };

  // Runs after the demo checkout modal's simulated payment succeeds.
  const completeDemoUpgrade = async () => {
    await api.post('/billing/checkout', { plan: checkoutPlan });
    await syncAfterChange();
  };

  const cancel = async () => {
    setPending('cancel');
    try {
      const { data: res } = await api.post('/billing/portal');
      if (res.demo) {
        toast.info('Switched back to Free. (demo)');
        await syncAfterChange();
      } else {
        window.location.href = res.url; // real Stripe billing portal
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not update plan.'));
      setPending(null);
    }
  };

  return (
    <div>
      <PageHeader title="Plans & billing" subtitle="Upgrade for more generations and features." icon={CreditCard} />

      <GlassCard spotlight className="mb-6 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-content-muted">
            <Sparkles className="h-4 w-4 text-brand-300" /> Current usage
          </div>
          <span className="rounded-full bg-brand-500/15 px-2.5 py-0.5 text-xs font-medium text-brand-300">
            {currentPlan} plan
          </span>
        </div>
        {!isLoading && data?.usage && (
          <>
            <p className="mt-2 font-display text-xl font-semibold">
              <CountUp value={data.usage.used} />
              <span className="text-base font-normal text-content-subtle"> / {data.usage.limit}</span>
            </p>
            <ProgressBar value={(data.usage.used / data.usage.limit) * 100} className="mt-2" />
          </>
        )}
      </GlassCard>

      {demo && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-brand-500/30 bg-brand-500/10 p-4 text-sm">
          <Zap className="mt-0.5 h-5 w-5 shrink-0 text-brand-300" />
          <p className="text-content-muted">
            <span className="font-medium text-content">Demo billing.</span> Upgrades apply instantly with no
            card and no charge — perfect for testing the flow. Add Stripe keys to enable real checkout.
          </p>
        </div>
      )}

      <StaggerGroup className="grid gap-5 lg:grid-cols-3">
        {TIERS.map((tier) => (
          <TierCard
            key={tier.id}
            tier={tier}
            isCurrent={tier.id === currentPlan}
            demo={demo}
            pending={pending}
            onUpgrade={upgrade}
            onCancel={cancel}
          />
        ))}
      </StaggerGroup>

      <DemoCheckout
        open={!!checkoutPlan}
        plan={TIERS.find((t) => t.id === checkoutPlan)}
        onClose={() => setCheckoutPlan(null)}
        onPaid={completeDemoUpgrade}
      />
    </div>
  );
}
